import prebidInstance from "./prebidSetup";  
import storeAllBids from "./storeAllBids"

function formatDatetime() {
    const now = new Date();
    return now.toISOString().slice(0, 19).replace("T", " "); // Converts to "YYYY-MM-DD HH:MM:SS"
}

class BidService {
  constructor() {
    this.pbjs = prebidInstance.pbjs;  //Use singleton instance
    this.subscribers = new Map();
    this.bidCache = new Map();
    this.activeAuctions = new Set();
    this.pendingQueue = [];
    this.BID_CACHE_TTL = 110000; // Cache TTL slightly less than refresh
    this.MAX_CONCURRENT_AUCTIONS = 2;

    this.init();
  }

  init() {
    if (this.pbjs?.requestBids) {
      this.setupEventListeners();
    } else {
      this.pbjs.que?.push(() => this.setupEventListeners());
    }

    setInterval(() => this.refreshAllBids(), 120000);
  }

  setupEventListeners() {
    this.pbjs.onEvent("auctionEnd", (data) => {
      if (data?.adUnitCode) {
        this.processBidResponse(data.adUnitCode);
      }
      this.processNextInQueue();
    });
  }

  subscribe(adUnitId, callback) {
    if (!this.subscribers.has(adUnitId)) {
      this.subscribers.set(adUnitId, new Set());
    }
    this.subscribers.get(adUnitId).add(callback);

    const cachedBid = this.getCachedBid(adUnitId);
    if (cachedBid) {
      setTimeout(() => callback(cachedBid), 0);
    } else {
      this.requestBidsForAdUnit(adUnitId);
    }

    return () => {
      const subs = this.subscribers.get(adUnitId);
      if (subs) {
        subs.delete(callback);
        if (subs.size === 0) {
          this.subscribers.delete(adUnitId);
        }
      }
    };
  }

  getCachedBid(adUnitId) {
    const cached = this.bidCache.get(adUnitId);
    if (cached && Date.now() - cached.timestamp < this.BID_CACHE_TTL) {
      return cached.data;
    }
    return null;
  }


requestBidsForAdUnit(adUnitId) {
    const adUnitArray = Array.isArray(adUnitId) ? adUnitId : [adUnitId];

    // Filter out already active ad units
    const filteredIds = adUnitArray.filter(id => !this.activeAuctions.has(id));

    if (filteredIds.length === 0) return;

    // Add them to the active set to avoid duplicates
    filteredIds.forEach(id => this.activeAuctions.add(id));
    
    this.pbjs.requestBids({
        adUnitCodes: filteredIds,
        timeout: 1000,
        bidsBackHandler: () => {
            for (const id of filteredIds) {
                this.processBidResponse(id);
                this.activeAuctions.delete(id);
            }
            this.processNextInQueue();
        }
    });
}

  processBidResponse(adUnitId) {
      const responses = this.pbjs.getBidResponsesForAdUnitCode(adUnitId);
      const bids = responses?.bids || [];

      if (!bids.length) {
          console.warn(`[bidService] No valid bids for ${adUnitId}`);
          this.notifySubscribers(adUnitId, { winningAd: null, bidder: null, cpm: null, isVideo: false });
          return;
      }

      //Capture auction start time
      const auctionStartTime = formatDatetime();

      // Find the winning bid (highest CPM)
      let topBid = bids.reduce((max, bid) => (bid.cpm > max.cpm ? bid : max), bids[0]);

      // Store all bids (even losing ones) in the database
    storeAllBids(adUnitId, bids, topBid, auctionStartTime);

      // Store only the winning bid for rendering
      const bidData = {
        winningAd: topBid.ad || topBid.vastXml || topBid.vastUrl || "",
        bidder: topBid.bidder || "No Bidder",
        cpm: topBid.cpm || 0,
        isVideo: Boolean(topBid.vastXml || topBid.vastUrl),
        ad_id: topBid.adId || "none",
      };

      //console.log(`[bidService] Storing winning bid for ${adUnitId}:`, bidData);
      this.bidCache.set(adUnitId, { data: bidData, timestamp: Date.now() });
      this.notifySubscribers(adUnitId, bidData);
  }

  notifySubscribers(adUnitId, bidData) {
    this.subscribers.get(adUnitId)?.forEach((callback) => {
      try {
        callback(bidData);
      } catch (error) {
        console.error(`[bidService] Error in subscriber callback for ${adUnitId}:`, error);
      }
    });
  }

  processNextInQueue() {
    if (this.pendingQueue.length === 0) return;

    // Take as many ad units as possible while respecting MAX_CONCURRENT_AUCTIONS
    const availableSlots = this.MAX_CONCURRENT_AUCTIONS - this.activeAuctions.size;
    if (availableSlots <= 0) return;

    const batchToProcess = this.pendingQueue.splice(0, availableSlots);

    this.requestBidsForAdUnit(batchToProcess);
}

  refreshAllBids() {
    const adUnitIds = Array.from(this.subscribers.keys());

    if (adUnitIds.length === 0) return;

    // Check if tab is active before refreshing bids
    if (document.hidden) {
        //console.log("[refreshAllBids] Skipping bid refresh, tab is inactive.");
        return;
    }

    // Staggered refresh to avoid overwhelming the server
    setTimeout(() => this.requestBidsForAdUnit(adUnitIds), Math.random() * 5000);
  }


}

const bidService = new BidService();
export default bidService;
